import CustomCheckBox from '@comp/CustomCheckbox';
import PayButtons from '@comp/PayButtons';
import RawFields from '@comp/RawFields';
import UserAgreement from '@comp/UserAgreement';
import { OnSubmit } from '@hooks/useOnSubmit';
import backBtn from '@images/custom/backbtnCustom.svg';
import { ContentData } from '@root/constants/formData/types';
import { cardMaskByNumber } from '@root/functions/cardMaskByNumber';
import { UserData } from '@root/hooks/useUserData';
import style from '@root/scss/eurasia36/index.module.scss';
import {
  excludeValidationForSomeCards,
  useValidation
} from '@root/utils/validation';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { Field } from './Field';
import InputWrapper from './InputWrapper';

export type FormInputs = {
  card_number: string;
  expiry: string;
  cvv: string;
  save: string;
  email: string;
  holder: string;
};

type FormProps = {
  onSubmit: OnSubmit;
  userData: UserData;
  children: React.ReactNode | undefined;
  formData: {
    title: string;
  };
  mainContent: ContentData['main'];
  expiry?: string;
  cardMask?: string;
};

export const Form = (props: FormProps) => {
  const {
    userData: {
      expiry,
      cardMask,
      apayEnabled,
      merchantHost,
      gpayEnabled,
      shouldDisplayAgreementDetails,
      shouldDisplayBackToSiteButton,
      merchantLink,
      options,
      fee,
      fullAmount,
      payAmount,
      tranType
    },
    onSubmit,
    children
  } = props;

  const [masked, setMasked] = useState(Boolean(cardMask));
  const [cardNumberMask, setCardNumberMask] = useState(
    cardMask ? '9999 99** **** 9999' : '9999 9999 9999 9999*'
  );

  const { cardValidation, isValidate, setIsValidate } = useValidation({
    noValidateExpiry: masked,
    noValidateCardNumber: masked,
    validateEmail: true,
    emailRequired: true,
    validateHolder: true
  });

  const {
    handleSubmit,
    watch,
    clearErrors,
    control,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<FormInputs>({
    resolver: cardValidation,
    defaultValues: {
      save: 'true',
      card_number: cardMask || '',
      expiry: expiry || '',
      cvv: '',
      holder: ''
    }
  });

  React.useEffect(() => {
    const subscription = watch((value) => {
      if (excludeValidationForSomeCards(value['card_number'])) {
        setIsValidate(false);
      } else if (!isValidate) {
        setIsValidate(true);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, isValidate]);

  const watchFields = watch('card_number');

  React.useEffect(() => {
    if (masked && watchFields != cardMask) {
      setMasked(false);
      setValue('card_number', '');
      setValue('expiry', '');
    }
  }, [masked, watchFields, cardMask]);

  React.useEffect(() => {
    const mask = cardMaskByNumber(watchFields, masked);
    if (mask != cardNumberMask) {
      setCardNumberMask(mask);
    }
  }, [watchFields]);

  useEffect(() => {
    if (cardValidation === undefined) {
      clearErrors();
    }
  }, [cardValidation]);

  const { t } = useTranslation(['common']);

  return (
    <section className={style.blockForm}>
      <form className={style.container_540} onSubmit={handleSubmit(onSubmit)}>
        {shouldDisplayBackToSiteButton && (
          <div
            onClick={() => {
              window.location.replace(merchantLink);
            }}
            className="back-button-custom-pay"
          >
            <img src={backBtn} alt="" />
            <span>{t('common:back')}</span>
          </div>
        )}
        {children ?? children}
        <div className={style.blockForm__form}>
          <Field
            control={control}
            name={'card_number'}
            label={t('common:card_number')}
            mask={cardNumberMask}
            error={errors['card_number']}
          />
          <div className={style.inout_row}>
            <Field
              disabled={masked}
              control={control}
              name={'expiry'}
              label={t('common:expiry')}
              mask={'99/99'}
              error={errors['expiry']}
            />
            <Field
              control={control}
              name={'cvv'}
              label="CVV"
              mask={'999'}
              error={errors['cvv']}
              inputMode={'numeric'}
              type="password"
              maskChar={''}
            />
          </div>
          <InputWrapper
            label={t('common:holder')}
            error={errors['holder']}
            control={control}
            name="holder"
          >
            <RawFields
              control={control}
              name={'holder'}
              inputMode={'text'}
              classNames={{ error: style.error_text }}
            />
          </InputWrapper>

          <div>
            <InputWrapper
              label={t('common:email', { defaultValue: 'Email' })}
              error={errors['email']}
              control={control}
              name="email"
            >
              <RawFields
                control={control}
                name={'email'}
                inputMode={'email'}
                classNames={{ error: style.error_text }}
              />
            </InputWrapper>
          </div>
        </div>
        {tranType == 'c2a' && (
          <>
            {shouldDisplayAgreementDetails && (
              <CustomCheckBox
                control={control}
                name={'userAgreedWithPolicy'}
                defaultValue={true}
              />
            )}
            <PayButtons
              isApple={apayEnabled}
              isGoogle={gpayEnabled}
              onSubmit={onSubmit}
              nomadLife={true}
              payInfo={{
                fee,
                clientName: options.clientName,
                agreementId: options.agreementId,
                merchantHost,
                fullAmount,
                payAmount
              }}
            />
          </>
        )}
        <div className="button-spin">
          <button
            className={`${style.btn} ${style.btn__purple}`}
            disabled={isSubmitting}
            type="submit"
          >
            {t('common:confirm')}
          </button>
          {isSubmitting && (
            <Oval
              wrapperClass="button-spin__spinner"
              strokeWidth={5}
              color="#fff"
            />
          )}
        </div>
        <UserAgreement />
      </form>
    </section>
  );
};
